import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  BUTTON_ADD_SELECTED_CONTENT,
  LOADING_CONTENT,
  MODAL_GALLERY_ARIA_LABEL,
  MODAL_GALLERY_EMPTY_CONTENT,
  PROCESSING_MEDIA_TO_USE,
  SAVING_MEDIA,
  GALLERY_SOURCE_SOCIAL_POSTS,
  GALLERY_SOCIAL_POSTS_DESCRIPTION,
  INSTAGRAM,
  FACEBOOK,
  PARTNERSHIP,
  INSTAGRAM_ACCOUNT_PLACEHOLDER,
  PAGE_FACEBOOK_PLACEHOLDER,
  FIELD_REQUIRED,
  SELECT_ACCOUNT_TO_LOAD_GALLERY_CONTENT,
  INFORMATION,
  SELECT_CONTENT_CONFIRMATION_TEXT,
  LINKEDIN,
  ADS_CAMPAIGN_LINKEDIN_USE_POST_VIDEO_DOWNLOAD_URL_MISSING,
} from "../../i18n/keysTranslations";
import { ConfirmAlert, SimpleAlert } from "../Alerts/Alerts";
import { BUTTON_SHADOW } from "../../utils/colors";
import { useTranslationApp } from "../../lib/i18next";
import useNearScreen from "../../hooks/useNearScreen";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useDebounced } from "../../hooks/useDebounce";
import ModalBasicLayout from "./ModalBasicLayout";
import Loader from "../Loaders/Loader";
import Button from "../Buttons/Button";
import Gallery from "../Gallery/Gallery";
import { usePagination } from "../../hooks/usePagination";
import {
  META,
  LINKEDIN as LINKEDIN_PLATFORM,
  ADS_CAMPAIGN_MEDIA_FROM_INSTAGRAM_POST,
  ADS_CAMPAIGN_MEDIA_FROM_FACEBOOK_POST,
  ADS_CAMPAIGN_MEDIA_FROM_PARTNERSHIP_POST,
  ADS_CAMPAIGN_MEDIA_FROM_LINKEDIN_POST,
  LINKEDIN_PUBLISH_AS,
  BORDER_RADIUS_BUTTON,
} from "../../utils/constants";
import { useRebrandBanner } from "../../hooks/useRebrandBanner";
import { startGetGalleryAdsPlatforms } from "../../actions/adsCampaigns";
import {
  startAddContentsToList,
  startSaveMediaInStorage,
} from "../../actions/user";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import Select from "../Form/Select";
import SelectInstagramAccountWithConnect from "../Meta/SelectInstagramAccountWithConnect";
import SelectFacebookPageWithConnect from "../Meta/SelectFacebookPageWithConnect";
import ModalGalleryAdsPlatformsLinkedInAccountSelect from "./ModalGalleryAdsPlatformsLinkedInAccountSelect";
import { useForm } from "react-hook-form";
import { useMetaAccountsLists } from "../../hooks/useMetaAccountsLists";
import {
  getLinkedInGalleryItemKey,
  getLinkedInPageAccountsForAdvertiser,
  getLinkedInPostStorageSourceUrl,
  normalizeLinkedInGalleryItem,
} from "../../services/linkedin";

const PAGE_SIZE = 10;
const LINKEDIN_PAGE_SIZE = 40;

const META_FILTER_OPTIONS = [
  { value: ADS_CAMPAIGN_MEDIA_FROM_INSTAGRAM_POST, label: INSTAGRAM },
  { value: ADS_CAMPAIGN_MEDIA_FROM_FACEBOOK_POST, label: FACEBOOK },
  {
    value: ADS_CAMPAIGN_MEDIA_FROM_PARTNERSHIP_POST,
    label: PARTNERSHIP,
  },
];

const ModalGalleryAdsPlatforms = ({
  modalOpen,
  multiple = true,
  onCloseModal,
  onCallbackSelectedItems,
  socialNetworkURL,
  pathStorage,
  contentFolderID = null,
  compact = false,
  from = ADS_CAMPAIGN_MEDIA_FROM_INSTAGRAM_POST,
  forceSocialPostsSource = false,
  forceLinkedInPostsSource = false,
  integrationID: integrationIDForced,
  adAccountID: adAccountIDForced,
  pageID: pageIDForced,
  linkedInIntegrationID: linkedInIntegrationIDForced,
  linkedInAdAccountID: linkedInAdAccountIDForced,
  adFormat,
}) => {
  const isForcedLinkedInSource = forceLinkedInPostsSource;
  const isLinkedInOrigin = from === ADS_CAMPAIGN_MEDIA_FROM_LINKEDIN_POST;
  const shouldLockLinkedInAccount =
    isForcedLinkedInSource || isLinkedInOrigin;
  const campaignLinkedInIntegrationID =
    linkedInIntegrationIDForced ||
    (shouldLockLinkedInAccount ? integrationIDForced : undefined);
  const campaignLinkedInAdAccountID =
    linkedInAdAccountIDForced ||
    (shouldLockLinkedInAccount ? adAccountIDForced : undefined);
  const [data, setData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadLastItem, setLoadLastItem] = useState(false);
  const [isCalling, setIsCalling] = useState(false);

  const { t } = useTranslationApp();

  const galleryRef = useRef(null);
  const abortControllerRef = useRef(null);
  const dataRef = useRef(data);
  const selectedItemsRef = useRef(selectedItems);
  const isUploadingRef = useRef(isUploading);
  dataRef.current = data;
  selectedItemsRef.current = selectedItems;
  isUploadingRef.current = isUploading;

  const { isNearScreen, fromRef } = useNearScreen({
    distance: "100px",
    once: false,
    disabledTimeout: true,
  });

  const dispatch = useDispatchApp();
  const { integrations } = useSelectorApp((state) => state.user);
  const { uniqueInstagramBusinessList, uniqueFacebookPagesList } =
    useMetaAccountsLists();
  const { totalTopOffsetPx } = useRebrandBanner();

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      metaFilter: from || ADS_CAMPAIGN_MEDIA_FROM_INSTAGRAM_POST,
      selectedInstagramAccount: null,
      selectedFacebookPage: null,
      platformsData: {
        linkedIn: {
          account: null,
        },
      },
    },
  });
  const metaFilter = watch("metaFilter");
  const selectedInstagramAccount = watch("selectedInstagramAccount");
  const selectedFacebookPage = watch("selectedFacebookPage");
  const isPicker = !multiple;
  const selectedLinkedInAccount = watch("platformsData.linkedIn.account");

  const isInstagramFilter =
    metaFilter === ADS_CAMPAIGN_MEDIA_FROM_INSTAGRAM_POST;
  const isFacebookOrPartnershipFilter =
    metaFilter === ADS_CAMPAIGN_MEDIA_FROM_FACEBOOK_POST ||
    metaFilter === ADS_CAMPAIGN_MEDIA_FROM_PARTNERSHIP_POST;
  const isLinkedInFilter = metaFilter === ADS_CAMPAIGN_MEDIA_FROM_LINKEDIN_POST;
  const isLinkedInGalleryMode = isForcedLinkedInSource || isLinkedInFilter;

  const campaignLinkedInPageOptions = useMemo(() => {
    if (!shouldLockLinkedInAccount || !campaignLinkedInIntegrationID) {
      return [];
    }

    const integration = integrations?.linkedIn?.[campaignLinkedInIntegrationID];
    return getLinkedInPageAccountsForAdvertiser({
      integration,
      integrationID: campaignLinkedInIntegrationID,
      adAccountID: campaignLinkedInAdAccountID,
    });
  }, [
    shouldLockLinkedInAccount,
    campaignLinkedInIntegrationID,
    campaignLinkedInAdAccountID,
    integrations?.linkedIn,
  ]);

  const showLinkedInPageSelector =
    isLinkedInGalleryMode &&
    (!shouldLockLinkedInAccount || campaignLinkedInPageOptions.length > 0);

  const selectedLinkedInAccountKey = useMemo(() => {
    if (!selectedLinkedInAccount?.integrationID) return "";
    return `${selectedLinkedInAccount.integrationID}:${selectedLinkedInAccount.id || ""}`;
  }, [selectedLinkedInAccount?.integrationID, selectedLinkedInAccount?.id]);

  const formatGalleryResponseItems = (items = []) => {
    if (isLinkedInGalleryMode) {
      return items.map((media) => normalizeLinkedInGalleryItem(media));
    }

    return items.map((media) => ({
      ...media,
      id: media.id,
    }));
  };

  const getGalleryItemKey = (item) => {
    if (!item) return "";
    if (isLinkedInGalleryMode) {
      return getLinkedInGalleryItemKey(item);
    }
    return item.id || "";
  };

  const onChangeLinkedInAccount = useCallback(
    (account) => {
      if (account) {
        setValue("platformsData.linkedIn.account", account);
      }
    },
    [setValue]
  );

  const getMetaGalleryRequestParams = () => {
    if (
      forceSocialPostsSource &&
      integrationIDForced &&
      adAccountIDForced &&
      pageIDForced
    ) {
      return {
        integrationID: integrationIDForced,
        adAccountID: adAccountIDForced,
        pageID: pageIDForced,
      };
    }
    if (isInstagramFilter && selectedInstagramAccount) {
      const fullAccount = uniqueInstagramBusinessList.find(
        (a) => a.instagramID === selectedInstagramAccount.instagramID
      );
      return {
        integrationID: selectedInstagramAccount.integrationID,
        adAccountID: fullAccount?.adAccountID,
        pageID: selectedInstagramAccount.pageID,
      };
    }
    if (isFacebookOrPartnershipFilter && selectedFacebookPage) {
      const fullPage = uniqueFacebookPagesList.find(
        (p) => p.id === selectedFacebookPage.id
      );
      return {
        integrationID: selectedFacebookPage.integrationID,
        adAccountID: fullPage?.adAccountID,
        pageID: selectedFacebookPage.id,
      };
    }
    return null;
  };

  const getLinkedInGalleryRequestParams = () => {
    const account = selectedLinkedInAccount;
    if (!account || typeof account !== "object" || !account.integrationID) {
      return null;
    }

    const publishAs = account.publishAs || LINKEDIN_PUBLISH_AS.ORGANIZATION;
    const params = {
      platform: LINKEDIN_PLATFORM,
      from: ADS_CAMPAIGN_MEDIA_FROM_LINKEDIN_POST,
      integrationID: account.integrationID,
      authorType: publishAs,
      ...(adFormat && { adFormat }),
    };

    if (publishAs === LINKEDIN_PUBLISH_AS.ORGANIZATION && account.id) {
      params.adAccountID = account.id;
    }

    return params;
  };

  const getGalleryRequestParams = () => {
    if (isLinkedInGalleryMode) {
      return getLinkedInGalleryRequestParams();
    }

    return getMetaGalleryRequestParams();
  };

  const getInitialData = async (signal) => {
    const params = getGalleryRequestParams();
    if (!params) {
      setLoading(false);
      setData([]);
      setSelectedItems([]);
      return;
    }

    setLoading(true);
    setData([]);
    setSelectedItems([]);

    const requestParams = isLinkedInGalleryMode
      ? {
          ...params,
          socialNetworkURL,
          lastKey: undefined,
          onChangeLastKey,
          onChangeLoadLastItem: setLoadLastItem,
          onChangeIsCalling: setIsCalling,
          limit: LINKEDIN_PAGE_SIZE,
          signal,
        }
      : {
          platform: META,
          integrationID: params.integrationID,
          adAccountID: params.adAccountID,
          pageID: params.pageID,
          socialNetworkURL,
          lastKey: undefined,
          onChangeLastKey,
          onChangeLoadLastItem: setLoadLastItem,
          onChangeIsCalling: setIsCalling,
          limit: PAGE_SIZE,
          from: metaFilter,
          signal,
        };

    const response = await dispatch(startGetGalleryAdsPlatforms(requestParams));
    if (response?.aborted) return;

    if (response.ok) {
      setData(formatGalleryResponseItems(response?.data));
    }

    setLoading(false);
  };

  const getNextMedia = useDebounced(async () => {
    if (loadLastItem || isCalling) return;

    const params = getGalleryRequestParams();
    if (!params) return;

    const signal = abortControllerRef.current?.signal;

    const requestParams = isLinkedInGalleryMode
      ? {
          ...params,
          socialNetworkURL,
          lastKey,
          onChangeLastKey,
          onChangeLoadLastItem: setLoadLastItem,
          onChangeIsCalling: setIsCalling,
          limit: LINKEDIN_PAGE_SIZE,
          signal,
        }
      : {
          platform: META,
          integrationID: params.integrationID,
          adAccountID: params.adAccountID,
          pageID: params.pageID,
          socialNetworkURL,
          lastKey,
          onChangeLastKey,
          onChangeLoadLastItem: setLoadLastItem,
          onChangeIsCalling: setIsCalling,
          limit: PAGE_SIZE,
          from: metaFilter,
          signal,
        };

    const response = await dispatch(startGetGalleryAdsPlatforms(requestParams));
    if (response?.aborted) return;
    if (response.ok) {
      setData((prevState) => [
        ...prevState,
        ...formatGalleryResponseItems(response?.data),
      ]);
    }
  }, 1000);

  const { lastKey, onChangeLastKey } = usePagination({
    rowsPerPageValue: PAGE_SIZE,
    onCallBackNextPage: getNextMedia,
  });

  useEffect(() => {
    if (isForcedLinkedInSource) {
      return;
    }

    abortControllerRef.current?.abort();
    setLoadLastItem(false);
    setData([]);
    setSelectedItems([]);
    setValue("selectedInstagramAccount", null);
    setValue("selectedFacebookPage", null);
    setValue("platformsData.linkedIn.account", null);
    setLoading(false);
    //eslint-disable-next-line
  }, [metaFilter]);

  useEffect(() => {
    if (
      !shouldLockLinkedInAccount ||
      !campaignLinkedInIntegrationID ||
      !modalOpen
    ) {
      return;
    }

    const integration = integrations?.linkedIn?.[campaignLinkedInIntegrationID];
    if (!integration) {
      return;
    }

    if (campaignLinkedInPageOptions.length === 1) {
      setValue(
        "platformsData.linkedIn.account",
        campaignLinkedInPageOptions[0]
      );
      return;
    }

    if (campaignLinkedInPageOptions.length > 1) {
      setValue("platformsData.linkedIn.account", null);
    }
    //eslint-disable-next-line
  }, [
    shouldLockLinkedInAccount,
    campaignLinkedInIntegrationID,
    modalOpen,
    campaignLinkedInPageOptions,
    integrations?.linkedIn,
  ]);

  useEffect(() => {
    if (
      !forceSocialPostsSource ||
      isForcedLinkedInSource ||
      !integrationIDForced ||
      !pageIDForced ||
      !modalOpen
    ) {
      return;
    }
    const isInstagram = metaFilter === ADS_CAMPAIGN_MEDIA_FROM_INSTAGRAM_POST;
    const isFacebookOrPartnership =
      metaFilter === ADS_CAMPAIGN_MEDIA_FROM_FACEBOOK_POST ||
      metaFilter === ADS_CAMPAIGN_MEDIA_FROM_PARTNERSHIP_POST;

    if (isInstagram) {
      const account = uniqueInstagramBusinessList.find(
        (a) =>
          a.integrationID === integrationIDForced && a.pageID === pageIDForced
      );
      const value = account
        ? {
            id: account.integrationID,
            name: account.name,
            integrationID: account.integrationID,
            instagramID: account.instagramID,
            pageID: account.pageID,
          }
        : null;
      setValue("selectedInstagramAccount", value);
      setValue("selectedFacebookPage", null);
    } else if (isFacebookOrPartnership) {
      const page = uniqueFacebookPagesList.find(
        (p) => p.integrationID === integrationIDForced && p.id === pageIDForced
      );
      const value = page
        ? {
            id: page.id,
            name: page.name,
            integrationID: page.integrationID,
            imageURL: page.imageURL,
          }
        : null;
      setValue("selectedFacebookPage", value);
      setValue("selectedInstagramAccount", null);
    }
    //eslint-disable-next-line
  }, [
    forceSocialPostsSource,
    integrationIDForced,
    pageIDForced,
    modalOpen,
    metaFilter,
    uniqueInstagramBusinessList,
    uniqueFacebookPagesList,
  ]);

  useEffect(() => {
    abortControllerRef.current?.abort();
    const params = getGalleryRequestParams();
    if (!params) {
      setLoading(false);
      setData([]);
      setSelectedItems([]);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    getInitialData(controller.signal);

    return () => {
      controller.abort();
    };
    //eslint-disable-next-line
  }, [
    selectedInstagramAccount,
    selectedFacebookPage,
    selectedLinkedInAccountKey,
  ]);

  useEffect(() => {
    if (isNearScreen && !loadLastItem) getNextMedia();
    //eslint-disable-next-line
  }, [isNearScreen]);

  const onFailedLoadUrl = (index) => {
    setData((prevState) => {
      const newData = [...prevState];
      const itemToRemove = newData[index];
      const itemKey = getGalleryItemKey(itemToRemove);
      if (itemKey) {
        setSelectedItems((prevSelected) =>
          prevSelected.filter((id) => id !== itemKey)
        );
      }
      const filteredData = newData.filter((item, i) => i !== index);
      return filteredData;
    });
  };

  const galleryData = data;

  const onSelectItem = (index) => {
    const item = galleryData[index];
    const itemKey = getGalleryItemKey(item);
    if (!item || !itemKey) return;

    setSelectedItems((prevSelected) => {
      if (prevSelected.includes(itemKey)) {
        return prevSelected.filter((id) => id !== itemKey);
      }

      if (multiple) {
        return [...prevSelected, itemKey];
      }

      return [itemKey];
    });
  };

  const addContentsToList = async (urlsStored) => {
    await dispatch(
      startAddContentsToList(
        urlsStored.filter(Boolean).map((url) => ({
          url,
          type: "upload",
          ...(contentFolderID && { folderID: contentFolderID }),
        }))
      )
    );
  };

  const onSendSelectedItems = async (indexes) => {
    const currentData = dataRef.current;
    const currentSelectedItems = selectedItemsRef.current;
    const dataSelectedItems = [];
    const itemsToProcess = indexes
      ? indexes.map((index) => currentData[index]).filter(Boolean)
      : currentSelectedItems
          .map((id) =>
            currentData.find((item) => getGalleryItemKey(item) === id)
          )
          .filter(Boolean);

    if (itemsToProcess.length === 0) {
      return false;
    }

    if (isLinkedInGalleryMode) {
      const sourceUrls = [];

      for (const item of itemsToProcess) {
        if (!item) continue;

        const sourceUrl = getLinkedInPostStorageSourceUrl({
          mediaType: item.mediaType,
          url: item.url,
          downloadUrl: item.downloadUrl,
        });

        if (!sourceUrl) {
          SimpleAlert({
            title: t(ADS_CAMPAIGN_LINKEDIN_USE_POST_VIDEO_DOWNLOAD_URL_MISSING),
            icon: "error",
          });
          return false;
        }

        sourceUrls.push(sourceUrl);
        dataSelectedItems.push({
          url: sourceUrl,
          from: ADS_CAMPAIGN_MEDIA_FROM_LINKEDIN_POST,
          postId: item.postId || item.postID,
          mediaType: item.mediaType,
          downloadUrl: item.downloadUrl,
          id: item.id,
          userID: item.userID || "",
        });
      }

      if (sourceUrls.length === 0) {
        return false;
      }

      setIsUploading(true);
      const response = await dispatch(
        startSaveMediaInStorage({
          urls: sourceUrls,
          path: pathStorage,
        })
      );

      if (!response.ok) {
        setIsUploading(false);
        return false;
      }

      const urlsStored = response.data.urls;

      urlsStored.forEach((urlStored, index) => {
        if (!urlStored || !dataSelectedItems[index]) return;
        dataSelectedItems[index].url = urlStored;
      });
      await addContentsToList(urlsStored);

      setIsUploading(false);
      await onCallbackSelectedItems(dataSelectedItems);
      onCloseModal();
      return true;
    }

    itemsToProcess.forEach((item) => {
      if (item) {
        dataSelectedItems.push({
          url: item.url,
          from: item.from || "gallery",
          platform: item.platform || null,
          id: item.id,
          userID: item.userID || "",
          isCarousel: item.isCarousel === true,
          carouselImages: item.carouselImages || null,
        });
      }
    });

    const urls = dataSelectedItems.map((item) => item.url).filter(Boolean);

    if (urls.length === 0) {
      return false;
    }

    setIsUploading(true);
    const response = await dispatch(
      startSaveMediaInStorage({
        urls,
        path: pathStorage,
      })
    );

    if (!response.ok) {
      setIsUploading(false);
      return false;
    }

    const urlsStored = response.data.urls;

    urlsStored.forEach((urlStored, index) => {
      if (!urlStored || !dataSelectedItems[index]) return;
      dataSelectedItems[index].url = urlStored;
    });
    await addContentsToList(urlsStored);

    setIsUploading(false);

    await onCallbackSelectedItems(dataSelectedItems);
    onCloseModal();
    return true;
  };

  const onSelectItemPicker = async (index) => {
    if (isUploadingRef.current) return;
    const item = dataRef.current[index];
    if (!getGalleryItemKey(item)) return;

    const result = await ConfirmAlert({
      title: t(INFORMATION),
      text: t(SELECT_CONTENT_CONFIRMATION_TEXT),
    });
    if (!result?.isConfirmed) return;

    await onSendSelectedItems([index]);
  };

  const selectedItemsAsIndexes = useMemo(() => {
    return galleryData
      .map((item, index) => {
        const itemKey = isLinkedInGalleryMode
          ? getLinkedInGalleryItemKey(item)
          : item?.id || "";
        return itemKey && selectedItems.includes(itemKey) ? index : null;
      })
      .filter((index) => index !== null);
  }, [galleryData, selectedItems, isLinkedInGalleryMode]);

  const filterOptions = isLinkedInOrigin
    ? [
        {
          value: ADS_CAMPAIGN_MEDIA_FROM_LINKEDIN_POST,
          label: LINKEDIN,
        },
      ]
    : forceSocialPostsSource
      ? META_FILTER_OPTIONS
      : [
          ...META_FILTER_OPTIONS,
          {
            value: ADS_CAMPAIGN_MEDIA_FROM_LINKEDIN_POST,
            label: LINKEDIN,
          },
        ];

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={compact ? true : !compact}
      fullScreen={compact ? false : !compact}
      maxWidth={compact ? "lg" : "xl"}
      scroll="paper"
      aria-label={t(MODAL_GALLERY_ARIA_LABEL)}
      sxContent={{
        backgroundColor: "fields.background",
        p: 0,
        pl: 3,
        pb: 2,
        mt: 1,
        overflowX: "hidden",
        "::-webkit-scrollbar": {
          width: 0,
          height: 0,
          background: "transparent",
        },
      }}
      refContent={galleryRef}
      title={t(GALLERY_SOURCE_SOCIAL_POSTS)}
      description={t(GALLERY_SOCIAL_POSTS_DESCRIPTION)}
    >
      <Box sx={{ mb: 3, pt: 1, display: "flex", flexDirection: "row", gap: 2 }}>
        {!isForcedLinkedInSource && (
          <Select
            name="metaFilter"
            variant="filled"
            control={control}
            doTranslate={true}
            disabled={isLinkedInOrigin}
            options={filterOptions}
            sx={{
              borderRadius: BORDER_RADIUS_BUTTON,
              width: 360,
              maxWidth: 360,
            }}
          />
        )}
        {!isForcedLinkedInSource && isInstagramFilter && (
          <SelectInstagramAccountWithConnect
            name="selectedInstagramAccount"
            variant="filled"
            label={t(INSTAGRAM_ACCOUNT_PLACEHOLDER)}
            fullWidth
            control={control}
            errors={errors}
            uniqueInstagramBusinessList={uniqueInstagramBusinessList}
            skipAutoSelect={true}
            disabled={forceSocialPostsSource}
            rules={{
              required: {
                value: true,
                message: t(FIELD_REQUIRED),
              },
            }}
            sxTextField={{
              width: 360,
              maxWidth: 360,
            }}
          />
        )}
        {!isForcedLinkedInSource && isFacebookOrPartnershipFilter && (
          <SelectFacebookPageWithConnect
            name="selectedFacebookPage"
            variant="filled"
            label={t(PAGE_FACEBOOK_PLACEHOLDER)}
            fullWidth
            control={control}
            errors={errors}
            uniqueFacebookPagesList={uniqueFacebookPagesList}
            skipAutoSelect={true}
            disabled={forceSocialPostsSource}
            rules={{
              required: {
                value: true,
                message: t(FIELD_REQUIRED),
              },
            }}
            sxTextField={{
              width: 360,
              maxWidth: 360,
            }}
          />
        )}
        {showLinkedInPageSelector && (
          <Box sx={{ width: 360, maxWidth: 360 }}>
            <ModalGalleryAdsPlatformsLinkedInAccountSelect
              control={control}
              errors={errors}
              t={t}
              onChangeAccount={onChangeLinkedInAccount}
              disableAutoSelect={true}
              disabled={
                shouldLockLinkedInAccount &&
                campaignLinkedInPageOptions.length === 1
              }
              restrictToCampaignAdvertiserPages={shouldLockLinkedInAccount}
              campaignIntegrationID={campaignLinkedInIntegrationID}
              campaignAdvertiserID={campaignLinkedInAdAccountID}
            />
          </Box>
        )}
      </Box>
      {!getGalleryRequestParams() && (
        <Box
          sx={{
            minHeight: 200,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ml: "-12px",
          }}
        >
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ fontSize: 18 }}
          >
            {t(SELECT_ACCOUNT_TO_LOAD_GALLERY_CONTENT)}
          </Typography>
        </Box>
      )}
      {loading && (
        <Loader size={70} hasMessage={true} message={t(LOADING_CONTENT)} />
      )}
      {!loading && getGalleryRequestParams() && (
        <Gallery
          data={galleryData}
          onCloseModal={onCloseModal}
          lastItemRef={fromRef}
          loadLastItem={loadLastItem}
          onFailedLoadUrl={onFailedLoadUrl}
          onSelectItem={isPicker ? onSelectItemPicker : onSelectItem}
          selectedItems={selectedItemsAsIndexes}
          onSelectPreview={() => {}}
          multiple={multiple}
          overlayPickerMode={isPicker}
          showFiltersSelector={false}
          showActionButtons={false}
          sxGallery={{
            px: 0,
          }}
          from={"posts"}
          acceptedFormats={[]}
          showDimensionsOverlay={true}
        />
      )}
      {!loading &&
        getGalleryRequestParams() &&
        galleryData?.length === 0 &&
        loadLastItem && (
          <Box
            sx={{
              height: `calc(100% - ${totalTopOffsetPx}px)`,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ml: "-12px",
            }}
          >
            <Typography variant="h4" align="center">
              {t(MODAL_GALLERY_EMPTY_CONTENT)}
            </Typography>
          </Box>
        )}

      {(selectedItems.length > 0 || isUploading) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: isUploading ? "#FFF" : "transparent",
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            py: 1,
            mt: 2,
            pb: 2,
          }}
        >
          {isUploading && (
            <LinearLoaderWithTime
              labels={[t(PROCESSING_MEDIA_TO_USE), t(SAVING_MEDIA)]}
              time={30 * 1000}
            />
          )}

          {!isUploading && !isPicker && (
            <Button
              color="primary"
              sx={{
                boxShadow: BUTTON_SHADOW,
                "&:hover": {
                  boxShadow: BUTTON_SHADOW,
                },
              }}
              onClick={(e) => {
                e?.preventDefault();
                e?.stopPropagation();
                onSendSelectedItems();
              }}
            >
              {t(BUTTON_ADD_SELECTED_CONTENT)}
            </Button>
          )}
        </Box>
      )}
    </ModalBasicLayout>
  );
};

export default ModalGalleryAdsPlatforms;
